<template>
    <div class="min-h-screen bg-gray-100 py-8">
        <Container class="max-w-4xl mx-auto">
            <div class="bg-white rounded-lg shadow-md p-8">
                <h1 class="text-3xl font-bold mb-8 text-center text-gray-800">
                    Create User (POC Only)
                </h1>
                
                <!-- Environment Check -->
                <div v-if="!isPOCEnvironment" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    <strong>Access Denied:</strong> This page is only available in POC environment.
                </div>
                
                <div v-else>
                    <!-- User Type Selection -->
                    <div class="mb-8">
                        <h2 class="text-xl font-semibold mb-4">Select User Type</h2>
                        <div class="flex space-x-4">
                            <label class="flex items-center">
                                <input 
                                    type="radio" 
                                    v-model="userType" 
                                    value="client-admin" 
                                    class="mr-2"
                                />
                                Client Admin
                            </label>
                            <label class="flex items-center">
                                <input 
                                    type="radio" 
                                    v-model="userType" 
                                    value="admin" 
                                    class="mr-2"
                                />
                                Admin
                            </label>
                        </div>
                    </div>

                    <!-- User Creation Form -->
                    <form @submit.prevent="createUser" class="space-y-6">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- First Name -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    First Name *
                                </label>
                                <input 
                                    type="text" 
                                    v-model="formData.firstName"
                                    required
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter first name"
                                />
                            </div>

                            <!-- Last Name -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Last Name *
                                </label>
                                <input 
                                    type="text" 
                                    v-model="formData.lastName"
                                    required
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter last name"
                                />
                            </div>

                            <!-- Email -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Email *
                                </label>
                                <input 
                                    type="email" 
                                    v-model="formData.email"
                                    required
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter email address"
                                />
                            </div>

                            <!-- Phone -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Phone
                                </label>
                                <input 
                                    type="tel" 
                                    v-model="formData.phone"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter phone number"
                                />
                            </div>

                            <!-- Role Title -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Role Title *
                                </label>
                                <input 
                                    type="text" 
                                    v-model="formData.roleTitle"
                                    required
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter role title"
                                />
                            </div>

                            <!-- Password -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Password *
                                </label>
                                <input 
                                    type="password" 
                                    v-model="formData.password"
                                    required
                                    minlength="6"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter password (min 6 characters)"
                                />
                            </div>
                        </div>

                        <!-- Client Admin Specific Fields -->
                        <div v-if="userType === 'client-admin'" class="space-y-4">
                            <h3 class="text-lg font-semibold text-gray-800">Client Admin Settings</h3>
                            
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <!-- Client ID -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">
                                        Client ID *
                                    </label>
                                    <input 
                                        type="text" 
                                        v-model="formData.client"
                                        required
                                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter client ID"
                                    />
                                </div>

                                <!-- User Can Login -->
                                <div class="flex items-center">
                                    <input 
                                        type="checkbox" 
                                        v-model="formData.userCanLogin"
                                        class="mr-2"
                                    />
                                    <label class="text-sm font-medium text-gray-700">
                                        User Can Login
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Admin Specific Fields -->
                        <div v-if="userType === 'admin'" class="space-y-4">
                            <h3 class="text-lg font-semibold text-gray-800">Admin Settings</h3>
                            
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Responsibilities
                                </label>
                                <textarea 
                                    v-model="formData.responsibilities"
                                    rows="3"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter admin responsibilities"
                                ></textarea>
                            </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="flex justify-center pt-6">
                            <button 
                                type="submit" 
                                :disabled="isCreating"
                                class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                <span v-if="isCreating" class="mr-2">
                                    <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                    </svg>
                                </span>
                                <span v-if="isCreating">Creating User...</span>
                                <span v-else>Create {{ userType === 'admin' ? 'Admin' : 'Client Admin' }} User</span>
                            </button>
                        </div>
                    </form>

                    <!-- Success/Error Messages -->
                    <div v-if="message" class="mt-6 p-4 rounded-md" :class="messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                        {{ message }}
                    </div>

                    <!-- Created Users List -->
                    <div v-if="createdUsers.length > 0" class="mt-8">
                        <h3 class="text-lg font-semibold mb-4">Recently Created Users</h3>
                        <div class="space-y-2">
                            <div 
                                v-for="user in createdUsers" 
                                :key="user.uid"
                                class="bg-gray-50 p-3 rounded border"
                            >
                                <div class="flex justify-between items-center">
                                    <div>
                                        <span class="font-medium">{{ user.firstName }} {{ user.lastName }}</span>
                                        <span class="text-gray-500 ml-2">({{ user.email }})</span>
                                    </div>
                                    <span class="text-sm text-gray-500">{{ user.type }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </div>
</template>

<script setup>
import Container from '../containers/Container.vue';
import { getFunctions, httpsCallable } from "firebase/functions";
import { getCurrentTeanantID } from "@/tenant";
import { ref, onMounted } from 'vue';

// Reactive data
const userType = ref('client-admin');
const isCreating = ref(false);
const message = ref('');
const messageType = ref('');
const createdUsers = ref([]);
const isPOCEnvironment = ref(false);

// Form data
const formData = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roleTitle: '',
    password: '',
    client: '',
    userCanLogin: true,
    responsibilities: ''
});

// Check if we're in POC environment
onMounted(async () => {
    try {
        const tenantId = await getCurrentTeanantID();
        isPOCEnvironment.value = tenantId === 'webcoda-jhxrc';
    } catch (error) {
        console.error('Error checking environment:', error);
        isPOCEnvironment.value = false;
    }
});

// Create user function
const createUser = async () => {
    if (!isPOCEnvironment.value) {
        message.value = 'This feature is only available in POC environment';
        messageType.value = 'error';
        return;
    }

    isCreating.value = true;
    message.value = '';
    messageType.value = '';

    try {
        const functions = getFunctions();
        const tenantId = await getCurrentTeanantID();
        
        let functionName, userData;
        
        if (userType.value === 'client-admin') {
            functionName = 'createNewUser';
            userData = {
                tenantId: tenantId,
                firstName: formData.value.firstName,
                lastName: formData.value.lastName,
                email: formData.value.email,
                phone: formData.value.phone,
                roleTitle: formData.value.roleTitle,
                password: formData.value.password,
                client: formData.value.client,
                accountSettings: {},
                userCanLogin: formData.value.userCanLogin
            };
        } else {
            functionName = 'createNewAdmin';
            userData = {
                tenantId: tenantId,
                firstName: formData.value.firstName,
                lastName: formData.value.lastName,
                email: formData.value.email,
                phone: formData.value.phone,
                roleTitle: formData.value.roleTitle,
                password: formData.value.password,
                responsibilities: formData.value.responsibilities
            };
        }

        const createUserFunction = httpsCallable(functions, functionName);
        const result = await createUserFunction({ data: userData });

        if (result.data.status === 200) {
            message.value = `User created successfully! UID: ${result.data.uid}`;
            messageType.value = 'success';
            
            // Add to created users list
            createdUsers.value.unshift({
                uid: result.data.uid,
                firstName: formData.value.firstName,
                lastName: formData.value.lastName,
                email: formData.value.email,
                type: userType.value === 'admin' ? 'Admin' : 'Client Admin'
            });
            
            // Reset form
            resetForm();
        } else {
            message.value = result.data.message || 'Failed to create user';
            messageType.value = 'error';
        }
    } catch (error) {
        console.error('Error creating user:', error);
        message.value = error.message || 'Failed to create user';
        messageType.value = 'error';
    } finally {
        isCreating.value = false;
    }
};

// Reset form
const resetForm = () => {
    formData.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        roleTitle: '',
        password: '',
        client: '',
        userCanLogin: true,
        responsibilities: ''
    };
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
